import { motion, useMotionValue, useAnimationFrame } from 'framer-motion';
import { useState, useRef, useEffect, useCallback } from 'react';

const testimonials = [
  {
    id: 1,
    position: 'IT Manager',
    company: 'Insurance Company',
    content: 'The ROI was immediate. Within the first month, the automated scanner detected critical vulnerabilities that our manual tests had missed. It is like having a security team working 24/7.',
    avatar: ''
  },
  {
    id: 2,
    position: 'Head of IT',
    company: 'Banking',
    content: 'Implementation took less than a day, and the results were eye-opening. The custom rules engine helped us enforce our security policies across all applications automatically.',
    avatar: ''
  },
  {
    id: 3,
    position: 'CTO',
    company: 'ISP Company',
    content: 'We reduced our security testing time by 70% while increasing coverage. The detailed reports and remediation guidance made it easy for our developers to fix issues quickly.',
    avatar: ''
  },
  {
    id: 4,
    position: 'Cybersecurity Analyst',
    company: 'Tech Innovators Inc.',
    content: 'The intuitive dashboard and actionable insights have transformed our security workflow. We can now identify and address threats with unprecedented speed and efficiency, giving us a competitive edge.',
    avatar: ''
  },
  {
    id: 5,
    position: 'Application Security Manager',
    company: 'Financial Services',
    content: 'Perfect for our compliance needs. The automated scans help us maintain PCI DSS compliance with minimal effort. It\'s been a game-changer for our security posture.',
    avatar: ''
  },
  {
    id: 6,
    position: 'CTO',
    company: 'Tech Startup',
    content: 'The customizable dashboard helps us prioritize vulnerabilities effectively. Our security team now focuses on strategic issues instead of routine scanning.',
    avatar: ''
  },
  {
    id: 7,
    position: 'Security Operations Manager',
    company: 'Telecommunications',
    content: 'As an insurance company, security is paramount. This tool\'s continuous monitoring gives us confidence that our customer data is protected around the clock.',
    avatar: ''
  },
  {
    id: 8,
    position: 'IT Security Manager',
    company: 'Healthcare Provider',
    content: 'We evaluated several solutions, but this one stood out for its ease of use and comprehensive coverage. The automated weekly reports keep our management informed without overwhelming them.',
    avatar: ''
  }
];

const Testimonials = () => {
  const [isRow1Paused, setIsRow1Paused] = useState(false);
  const [isRow2Paused, setIsRow2Paused] = useState(false);
  const row1TimeoutRef = useRef<number | null>(null);
  const row2TimeoutRef = useRef<number | null>(null);

  const row1X = useMotionValue(0);
  const row2X = useMotionValue(-50 * 16 * testimonials.slice(4).length);

  const row1Speed = 32 / 60;
  const row2Speed = 32 / 60;

  const row1TotalDistance = 50 * 16 * testimonials.length;
  const row2TotalDistance = 50 * 16 * testimonials.slice(4).length;

  useAnimationFrame(useCallback(() => {
    if (!isRow1Paused) {
      const currentX1 = row1X.get();
      const newX1 = currentX1 - row1Speed;
      if (newX1 <= -row1TotalDistance) {
        row1X.set(0);
      } else {
        row1X.set(newX1);
      }
    }

    if (!isRow2Paused) {
      const currentX2 = row2X.get();
      const newX2 = currentX2 + row2Speed;
      if (newX2 >= 0) {
        row2X.set(-row2TotalDistance);
      } else {
        row2X.set(newX2);
      }
    }
  }, [isRow1Paused, isRow2Paused, row1X, row2X, row1Speed, row2Speed, row1TotalDistance, row2TotalDistance]));

  const handleRow1MouseEnter = () => {
    setIsRow1Paused(true);
    if (row1TimeoutRef.current) {
      clearTimeout(row1TimeoutRef.current);
    }
  };

  const handleRow1MouseLeave = () => {
    if (row1TimeoutRef.current) {
      clearTimeout(row1TimeoutRef.current);
    }
    row1TimeoutRef.current = setTimeout(() => {
      setIsRow1Paused(false);
    }, 3000);
  };

  const handleRow2MouseEnter = () => {
    setIsRow2Paused(true);
    if (row2TimeoutRef.current) {
      clearTimeout(row2TimeoutRef.current);
    }
  };

  const handleRow2MouseLeave = () => {
    if (row2TimeoutRef.current) {
      clearTimeout(row2TimeoutRef.current);
    }
    row2TimeoutRef.current = setTimeout(() => {
      setIsRow2Paused(false);
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (row1TimeoutRef.current) {
        clearTimeout(row1TimeoutRef.current);
      }
      if (row2TimeoutRef.current) {
        clearTimeout(row2TimeoutRef.current);
      }
    };
  }, []);

  return (
    <section className="py-20 bg-gray-50 overflow-hidden">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
            What Our Clients
            <span className="block bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mt-2">
              Say
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mt-6">
            Hear how developers, tech leads and project managers rely on Atlas Defenders to secure their applications and infrastructure
          </p>
        </motion.div>

        <div className="relative">
          <motion.div
            className="flex space-x-6 mb-6"
            style={{ x: row1X }}
          >
            {[...testimonials, ...testimonials, ...testimonials].map((testimonial, index) => (
              <motion.div
                key={`row1-${index}`}
                className="flex-shrink-0 w-96 bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 select-none cursor-default"
                whileHover={{ scale: 1.02, y: -5 }}
                onMouseEnter={handleRow1MouseEnter}
                onMouseLeave={handleRow1MouseLeave}
              >
                <div className="mb-6">
                  <p className="text-gray-700 text-base leading-relaxed font-light">
                    {testimonial.content}
                  </p>
                </div>

                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-400 rounded-full flex items-center justify-center mr-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <img
                        src={testimonial.avatar}
                        alt="Client Avatar"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = `<div class="w-full h-full bg-cyan-400/20 flex items-center justify-center"><svg class="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path></svg></div>`;
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">
                      {testimonial.position}
                    </div>
                    <div className="text-cyan-400 text-sm font-medium">
                      {testimonial.company}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="flex space-x-6"
            style={{ x: row2X }}
          >
            {[...testimonials.slice(4), ...testimonials.slice(4), ...testimonials.slice(4)].map((testimonial, index) => (
              <motion.div
                key={`row2-${index}`}
                className="flex-shrink-0 w-96 bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 select-none cursor-default"
                whileHover={{ scale: 1.02, y: -5 }}
                onMouseEnter={handleRow2MouseEnter}
                onMouseLeave={handleRow2MouseLeave}
              >
                <div className="mb-6">
                  <p className="text-gray-700 text-base leading-relaxed font-light">
                    {testimonial.content}
                  </p>
                </div>

                <div className="flex items-center">
                   <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-400 rounded-full flex items-center justify-center mr-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <img
                        src={testimonial.avatar}
                        alt="Client Avatar"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = `<div class="w-full h-full bg-cyan-400/20 flex items-center justify-center"><svg class="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path></svg></div>`;
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">
                      {testimonial.position}
                    </div>
                    <div className="text-cyan-400 text-sm font-medium">
                      {testimonial.company}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
